import * as Sentry from '@sentry/angular';
import { trigger } from '@angular/animations';
import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { INotification } from './models/INotification';
import { AuthService } from './services/auth/auth.service';
import { loadCalculationLinks, loadPlans, resetNotification, storeUserData, setNotification, removeUserData } from './store/actions/app/app.actions';
import { IAppState } from './store/reducers/app/app.reducer';
import { inOutAnimation, onlyOutAnimation } from './utils/animations/animations';
import { URLS_WITHOUT_FOOTER, URLS_WITHOUT_NAVBAR_OPTIONS } from './utils/constants';
import { DataLayerService } from './services/window/data-layer.service';
import { UserLogsService } from './services/userlogs/userlogs.service';
import { NetworkService } from './services/network/network.service';
import { environment } from 'src/environments/environment';
import { createChat } from '@n8n/chat';
import { IUser } from './models/IUser';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
  animations: [
    trigger('onlyOutAnimation', onlyOutAnimation),
    trigger('inOutAnimation', inOutAnimation)
  ]
})

export class AppComponent implements OnInit {
  showAuthModal = false;
  title = 'sismat-frontend-app';
  release = environment.sentry.release;

  urlsWithoutFooter = URLS_WITHOUT_FOOTER;
  urlsWithoutNavbarOptions = URLS_WITHOUT_NAVBAR_OPTIONS;

  mercadopagoData$: Observable<string> = this.store.select(
    (state) => state.app.mercadopagoData
  );

  notification$: Observable<INotification> = this.store.select(
    (state) => state.app.notification
  );

  user$: Observable<IUser> = this.store.select(
    (state) => state.app.user
  );

  notification = null;
  hasNetworkError$ = this.networkService.hasNetworkError$;
  location = window.location;

  constructor(
    private store: Store<{ app: IAppState }>,
    private authService: AuthService,
    private router: Router,
    private titleService: Title,
    private dataLayerService: DataLayerService,
    private userLogService: UserLogsService,
    private networkService: NetworkService
  ) {
    this.router.events.subscribe(event=> { 
        if (event instanceof NavigationEnd)
        {
          this.dataLayerService.logPageView(event.url);
        }
    });
  }

  async ngOnInit() {
    console.log('AppComponent getUser');
    this.authService.getUser().subscribe({
      next: async (user) => {
        if(user) {
          this.store.dispatch(storeUserData({ user }))
        } else {
          this.store.dispatch(removeUserData());
          //this.initializeChat();
        }
      }, 
      error: (error) => {
      }
    });
    this.authService.resetUserSubject.subscribe({
      next: async () => {
        console.log('AppComponent resetUserSubject');
        const user  = await this.authService.getUser().pipe(take(1)).toPromise();
        this.store.dispatch(storeUserData({ user }))
        //this.initializeChat(user);
      }, 
      error: (error) => {
        const notification : INotification = {
          title: 'Error',
          text: "Error al obtener la información del sistema. Por favor recargue la página e intente nuevamente",
          type: "danger"
        }

        this.store.dispatch(setNotification({ notification }));
      } 
    })

    this.store.dispatch(loadCalculationLinks());
    this.titleService.setTitle('Sismat - El Portal de la Construccion')

    this.user$
    .subscribe({
      next: (data) => {
        Sentry.setUser(data?.id ? { id: String(data.id) } : null);
        if(data) {
          //this.initializeChat(data)
        }
      },
      error: (error) => {
        console.log(error);
      }
    })

    this.mercadopagoData$
    .subscribe({
      next: (data) => {
        if(data && data['init_point']) {
          window.location.href = data['init_point'];
        }
      }, 
      error: (error) => {
        const notification : INotification = {
          title: 'Error',
          text: "Error al obtener la información de la suscripción. Por favor recargue la página e intente nuevamente",
          type: "danger"
        }

        this.store.dispatch(setNotification({ notification }));
      } 
    });

    this.notification$
    .subscribe({
      next: (data) => {
        if(data) {
          this.notification = data;
          setTimeout(() => {
            this.notification = null;
            this.store.dispatch(resetNotification());
          }, 5000)
        }
      }, 
      error: (error) => {
        const notification : INotification = {
          title: 'Error',
          text: "Error al obtener la información del sistema. Por favor recargue la página e intente nuevamente",
          type: "danger"
        }

        this.store.dispatch(setNotification({ notification }));
      } 
    });

    this.store.dispatch(loadPlans());
  }

  showFooter() {
    return !this.urlsWithoutFooter.some(url => this.router.url.includes(url))
  }

  showNavbarOptions() {
    return !this.urlsWithoutNavbarOptions.some(url => this.router.url.includes(url))
  }

  showFiscalData() {
    //return true or false only if its exactly the root page 
    return this.router.url === '/';    
  }

  initializeChat(user = null) {
    createChat({
      webhookUrl: environment.n8nChatWebhookUrl,
      defaultLanguage: 'en',
      initialMessages: [
        `👋 Hola${user && user.first_name ? ` ${user?.first_name}` : ''}! Soy el asistente virtual de SISMAT.`,
        `¿En qué puedo ayudarte?`,
      ],
      i18n: {
        en: {
          title: '🤖 Asistente Sismat',
          subtitle: "🟢 En Linea - Las respuestas son autogeneradas y pueden contener errores. Verificá siempre la información.",
          footer: '',
          getStarted: 'Nueva conversación',
          inputPlaceholder: 'Escribe tu pregunta..',
          closeButtonTooltip: 'Cerrar'
        },
      },
      showWindowCloseButton: true,
      ...(!!user ? { metadata: { email: user.email, user_id: user.id, first_name: user.first_name, is_suscribed: user.is_suscribed, plan_due: user.plan_due }} : {})
    });
  }
}
